import { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, RefreshCcw, X, ChevronRight, ChevronDown } from "lucide-react";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import {
  CustomDetailPanel,
  HttpStatusBadge,
  OkBadge,
  type CustomResultData,
} from "./audit-custom-detail-panel";
import {
  buildAuditCsvUrl,
  buildAuditQueryString,
  computeVisibleTools,
  resolveEffectiveEntityId,
  resolveEffectiveToolId,
} from "./audit-tool-invocations-filters";
import {
  createAuditCsvSummaryState,
  feedAuditCsvSummary,
  finalizeAuditCsvSummary,
} from "./audit-tool-invocations-csv-summary";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface ToolInvocation {
  id: string;
  toolId: string;
  chatbotId: string;
  userId: string;
  status: string;
  durationMs: number | null;
  errorMessage: string | null;
  argsJson: string | null;
  resultJson: string | null;
  createdAt: string;
}

interface ToolRow {
  id: string;
  name: string;
  displayName: string;
  // Server-tagged grouping key: "builtin" | "servicenow" | "workday" | "custom:<slug>".
  integration?: string;
}
interface ChatbotRow { id: string; name: string }
interface UserRow { id: string; name: string; email: string }
interface IntegrationRow { id: string; type: string; name: string }

const BUILTIN_INTEGRATION_LABELS: Record<string, string> = {
  builtin: "Built-in tools",
  servicenow: "ServiceNow",
  workday: "Workday",
};

// Read a query param off the current URL on mount (e.g. when admins land
// here from Settings → Integrations via "View audit log", or when they
// share/bookmark a filtered view). SSR-safely no-ops if `window` is
// unavailable. `fallback` is returned for missing/empty values so each
// filter can use its own "neutral" default ("all" vs "").
function readInitialParam(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = new URLSearchParams(window.location.search).get(name);
  return v && v.trim() ? v : fallback;
}

export default function AuditToolInvocationsPage() {
  const [status, setStatus] = useState<string>(() => readInitialParam("status", "all"));
  const [chatbotId, setChatbotId] = useState<string>(() => readInitialParam("chatbotId", "all"));
  const [integration, setIntegration] = useState<string>(() => readInitialParam("integration", "all"));
  const [toolId, setToolId] = useState<string>(() => readInitialParam("toolId", "all"));
  const [userId, setUserId] = useState<string>(() => readInitialParam("userId", "all"));
  const [from, setFrom] = useState<string>(() => readInitialParam("from", ""));
  const [to, setTo] = useState<string>(() => readInitialParam("to", ""));
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const prettify = (raw: string | null | undefined) => {
    if (!raw) return "—";
    try { return JSON.stringify(JSON.parse(raw), null, 2); }
    catch { return raw; }
  };

  const { data: tools } = useQuery<ToolRow[]>({ queryKey: ["/api/tools"] });
  const { data: chatbots } = useQuery<ChatbotRow[]>({ queryKey: ["/api/chatbots"] });
  const { data: users } = useQuery<UserRow[]>({ queryKey: ["/api/org/users"] });
  const { data: integrations } = useQuery<IntegrationRow[]>({ queryKey: ["/api/integrations"] });

  // Build the integration dropdown: ServiceNow / Workday / Built-in are
  // always offered (so admins can filter them even if no integration row
  // exists), and every custom integration the org has configured shows
  // up by its admin-given name (`custom:<slug>` keyed).
  const integrationOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [
      { value: "builtin", label: BUILTIN_INTEGRATION_LABELS.builtin },
      { value: "servicenow", label: BUILTIN_INTEGRATION_LABELS.servicenow },
      { value: "workday", label: BUILTIN_INTEGRATION_LABELS.workday },
    ];
    integrations?.forEach((row) => {
      if (row.type?.startsWith("custom:")) {
        opts.push({ value: row.type, label: row.name || row.type });
      }
    });
    return opts;
  }, [integrations]);

  // When an integration is picked, narrow the Tool dropdown to only
  // tools owned by that integration. This keeps the per-tool filter
  // useful instead of flooding admins with every tool in the org.
  const visibleTools = useMemo(
    () => computeVisibleTools(tools, integration),
    [tools, integration],
  );

  // If the currently-picked tool no longer matches the integration
  // filter, drop the tool selection so we don't send a stale toolId.
  const effectiveToolId = useMemo(
    () => resolveEffectiveToolId(toolId, visibleTools),
    [toolId, visibleTools],
  );

  // Same safety net for the Bot and User dropdowns: if the picked row
  // no longer exists in the latest /api/chatbots or /api/org/users
  // response (deleted, or filtered out by a permissions change), drop
  // the filter so we don't keep sending an orphan id and silently
  // produce an empty audit table.
  const effectiveChatbotId = useMemo(
    () => resolveEffectiveEntityId(chatbotId, chatbots),
    [chatbotId, chatbots],
  );
  const effectiveUserId = useMemo(
    () => resolveEffectiveEntityId(userId, users),
    [userId, users],
  );

  const queryString = useMemo(
    () =>
      buildAuditQueryString(
        { status, chatbotId, integration, toolId, userId, from, to },
        tools,
        chatbots,
        users,
      ),
    [status, chatbotId, integration, toolId, userId, from, to, tools, chatbots, users],
  );

  // Mirror the active filters back into the URL so admins can bookmark
  // or share the link and have it restore the same view. We use
  // `replaceState` (not `pushState`) so toggling filters doesn't pollute
  // browser history. Cleared filters drop out of the URL entirely. We
  // intentionally don't sync `search` (it's a client-side text filter
  // applied after the fetch) or `limit` (a fixed page size, not a
  // user-facing knob).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = new URLSearchParams(window.location.search);
    const apply = (key: string, value: string, neutral: string) => {
      if (value && value !== neutral) p.set(key, value);
      else p.delete(key);
    };
    apply("status", status, "all");
    apply("chatbotId", effectiveChatbotId, "all");
    apply("integration", integration, "all");
    apply("toolId", effectiveToolId, "all");
    apply("userId", effectiveUserId, "all");
    apply("from", from, "");
    apply("to", to, "");
    const qs = p.toString();
    const next = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== current) {
      window.history.replaceState(window.history.state, "", next);
    }
  }, [status, effectiveChatbotId, integration, effectiveToolId, effectiveUserId, from, to]);

  const { data: rows, isLoading, refetch, isFetching } = useQuery<ToolInvocation[]>({
    queryKey: ["/api/audit/tool-invocations", { status, chatbotId: effectiveChatbotId, integration, toolId: effectiveToolId, userId: effectiveUserId, from, to }],
    // apiRequest attaches the Bearer JWT — a raw fetch here would hit
    // the API unauthenticated (no auth cookie exists in this app) and
    // 401, rendering the table as a false "no invocations" empty state.
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/audit/tool-invocations?${queryString}`);
      return r.json();
    },
  });

  const toolById = useMemo(() => {
    const m = new Map<string, ToolRow>();
    tools?.forEach((t) => m.set(t.id, t));
    return m;
  }, [tools]);
  const integrationLabelByKey = useMemo(() => {
    const m = new Map<string, string>();
    integrationOptions.forEach((o) => m.set(o.value, o.label));
    return m;
  }, [integrationOptions]);
  const chatbotNameById = useMemo(() => {
    const m = new Map<string, string>();
    chatbots?.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [chatbots]);
  const userLabelById = useMemo(() => {
    const m = new Map<string, string>();
    users?.forEach((u) => m.set(u.id, u.name || u.email));
    return m;
  }, [users]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (toolById.get(r.toolId)?.displayName || "").toLowerCase().includes(q) ||
      (userLabelById.get(r.userId) || r.userId).toLowerCase().includes(q) ||
      (chatbotNameById.get(r.chatbotId) || r.chatbotId).toLowerCase().includes(q) ||
      (r.errorMessage || "").toLowerCase().includes(q),
    );
  }, [rows, search, toolById, chatbotNameById, userLabelById]);

  // Drive the "Export CSV" button.
  //
  // The app authenticates with a Bearer JWT (no auth cookie is ever
  // issued at login), so a plain <a href> download would reach the
  // API unauthenticated and 401. Instead we make ONE authenticated
  // fetch and use it for both jobs:
  //   1. the raw bytes are collected chunk-by-chunk and saved to
  //      disk via a Blob + object-URL anchor (same pattern as the
  //      call-report and chatbot-report exports), and
  //   2. the same chunks are decoded through a tiny CSV state
  //      machine that counts rows and detects the synthetic
  //      `,,,,truncated,…` marker the server appends when the
  //      100k-row cap was hit, driving the confirmation toast.
  // Single request means the saved file and the toast can never
  // disagree. Blob parts are handed to the browser's blob storage
  // (which may spill to disk), and the server truncates each
  // args/result field to ~4 KB, so even large exports stay within
  // what evergreen browsers handle for programmatic downloads.
  const downloadCsv = async () => {
    if (isExporting) return;
    const filters = { status, chatbotId, integration, toolId, userId, from, to };
    const csvUrl = buildAuditCsvUrl(filters, tools, chatbots, users);

    setIsExporting(true);
    try {
      // apiRequest attaches the Bearer token and throws a friendly
      // error message on non-2xx responses.
      const res = await apiRequest("GET", csvUrl);
      // Cap reported by the server alongside the body. Falling back
      // to "the cap" lets the toast still render a reasonable
      // message if a future server build stops emitting the header,
      // but production always sets it.
      const capHeader = res.headers.get("X-Audit-Row-Cap");
      const cap = capHeader ? Number.parseInt(capHeader, 10) : Number.NaN;
      const summaryState = createAuditCsvSummaryState();
      const chunks: BlobPart[] = [];
      const body = res.body;
      if (body) {
        // TextDecoder in streaming mode handles the UTF-8 BOM and
        // any multibyte characters that straddle chunk boundaries.
        // Each raw chunk is kept for the Blob save while its decoded
        // text is fed to the row counter and then dropped.
        const decoder = new TextDecoder("utf-8");
        const reader = body.getReader();
        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            if (value) {
              chunks.push(value);
              feedAuditCsvSummary(summaryState, decoder.decode(value, { stream: true }));
            }
          }
          const tail = decoder.decode();
          if (tail) feedAuditCsvSummary(summaryState, tail);
        } finally {
          reader.releaseLock();
        }
      } else {
        // Older browsers without ReadableStream on Response: fall
        // back to .text(). The audit page is admin-only and we
        // already require evergreen browsers, so this is a safety
        // net rather than a hot path.
        const text = await res.text();
        chunks.push(text);
        feedAuditCsvSummary(summaryState, text);
      }

      // Save the exact bytes we just streamed. Prefer the server's
      // Content-Disposition filename; fall back to a local stamp.
      const dispo = res.headers.get("Content-Disposition") ?? "";
      const nameMatch = /filename="?([^";]+)"?/i.exec(dispo);
      const filename = nameMatch?.[1] ?? `tool-invocations-${new Date().toISOString().replace(/[:.]/g, "").slice(0, 15)}Z.csv`;
      const blobUrl = URL.createObjectURL(new Blob(chunks, { type: "text/csv;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);

      const summary = finalizeAuditCsvSummary(summaryState);
      const rowsLabel = `${summary.dataRows.toLocaleString("en-US")} ${summary.dataRows === 1 ? "row" : "rows"}`;
      if (summary.truncated) {
        // Cap-hit: surface it as a destructive toast so the warning
        // can't be missed, and tell admins exactly how to recover
        // (narrow the date range and re-export).
        const capLabel = Number.isFinite(cap) ? cap.toLocaleString("en-US") : "row";
        toast({
          variant: "destructive",
          title: Number.isFinite(cap)
            ? `Export hit the ${capLabel}-row cap`
            : "Export hit the row cap",
          description: `Saved ${rowsLabel}, but earlier activity was cut off. Narrow the date range and export again to pull the rest.`,
        });
      } else {
        toast({
          title: "Export complete",
          description: `Saved ${rowsLabel} for the current filters.`,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        variant: "destructive",
        title: "Export failed",
        description: message,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const clearFilters = () => {
    setStatus("all"); setChatbotId("all"); setIntegration("all"); setToolId("all");
    setUserId("all"); setFrom(""); setTo(""); setSearch("");
  };

  const statusBadge = (s: string) => {
    if (s === "success") return <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">success</Badge>;
    if (s === "pending_approval") return <Badge variant="secondary">pending</Badge>;
    if (s === "rejected" || s === "denied") return <Badge variant="destructive">{s}</Badge>;
    if (s === "rate_limited") return <Badge variant="outline">rate limited</Badge>;
    if (s === "error") return <Badge variant="destructive">error</Badge>;
    return <Badge variant="outline">{s}</Badge>;
  };

  // Custom integration rows store `{ status, ok, truncated, body }` (and
  // sometimes `{ error }`) in resultJson. Pull those out for the detail
  // pane so reviewers see HTTP status / ok-failed at a glance instead of
  // hunting through the raw JSON. Returns null for rows that aren't from
  // a custom integration (or whose payload isn't decodable).
  const customDetailFor = (r: ToolInvocation): CustomResultData | null => {
    const tool = toolById.get(r.toolId);
    const isCustom = (tool?.integration ?? "").startsWith("custom:")
      || tool?.name?.startsWith("custom__");
    if (!isCustom || !r.resultJson) return null;
    try {
      const parsed = JSON.parse(r.resultJson);
      if (parsed && typeof parsed === "object") return parsed as CustomResultData;
      return null;
    } catch {
      return null;
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tool audit log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every action your bots take on behalf of staff is recorded here, including denials and approvals.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>
            Narrow the log, then export to CSV. Exports stream up to 100,000
            rows at a time; if the cap is hit the file ends with a
            <code className="mx-1 px-1 rounded bg-muted text-foreground">truncated</code>
            marker row so you know to narrow the date range.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <span id="audit-filter-status-label" className="text-xs text-muted-foreground">Status</span>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger aria-labelledby="audit-filter-status-label" data-testid="select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="pending_approval">Pending approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="denied">Denied (role)</SelectItem>
                  <SelectItem value="rate_limited">Rate limited</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <span id="audit-filter-bot-label" className="text-xs text-muted-foreground">Bot</span>
              <Select value={effectiveChatbotId} onValueChange={setChatbotId}>
                <SelectTrigger aria-labelledby="audit-filter-bot-label" data-testid="select-chatbot"><SelectValue placeholder="All bots" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All bots</SelectItem>
                  {chatbots?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <span id="audit-filter-integration-label" className="text-xs text-muted-foreground">Integration</span>
              <Select value={integration} onValueChange={(v) => { setIntegration(v); setToolId("all"); }}>
                <SelectTrigger aria-labelledby="audit-filter-integration-label" data-testid="select-integration"><SelectValue placeholder="All integrations" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All integrations</SelectItem>
                  {integrationOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`option-integration-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <span id="audit-filter-tool-label" className="text-xs text-muted-foreground">Tool</span>
              <Select value={effectiveToolId} onValueChange={setToolId}>
                <SelectTrigger aria-labelledby="audit-filter-tool-label" data-testid="select-tool"><SelectValue placeholder="All tools" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tools</SelectItem>
                  {visibleTools.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`option-tool-${t.name}`}>
                      {t.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <span id="audit-filter-user-label" className="text-xs text-muted-foreground">User</span>
              <Select value={effectiveUserId} onValueChange={setUserId}>
                <SelectTrigger aria-labelledby="audit-filter-user-label" data-testid="select-user"><SelectValue placeholder="All users" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  {users?.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label htmlFor="audit-filter-from" className="text-xs text-muted-foreground">From</label>
              <Input id="audit-filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="input-from" />
            </div>
            <div className="space-y-1">
              <label htmlFor="audit-filter-to" className="text-xs text-muted-foreground">To</label>
              <Input id="audit-filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="input-to" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label htmlFor="audit-filter-search" className="text-xs text-muted-foreground">Search</label>
              <Input
                id="audit-filter-search"
                data-testid="input-audit-search"
                placeholder="tool name, user, bot, error…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" onClick={() => refetch()} data-testid="button-refresh-audit">
              <RefreshCcw className={`w-4 h-4 mr-1 ${isFetching ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="outline" onClick={clearFilters} data-testid="button-clear-filters">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
            <Button
              onClick={downloadCsv}
              data-testid="button-export-csv"
              disabled={isExporting}
            >
              <Download className={`w-4 h-4 mr-1 ${isExporting ? "animate-pulse" : ""}`} />
              {isExporting ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {[0,1,2,3,4].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No invocations match your filters.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"><span className="sr-only">Expand row</span></TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Tool</TableHead>
                  <TableHead>Integration</TableHead>
                  <TableHead>Bot</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const isOpen = expanded.has(r.id);
                  const tool = toolById.get(r.toolId);
                  const integrationKey = tool?.integration ?? "builtin";
                  const integrationLabel = integrationLabelByKey.get(integrationKey)
                    ?? (integrationKey.startsWith("custom:") ? integrationKey.slice(7) : integrationKey);
                  const custom = customDetailFor(r);
                  return (
                    <Fragment key={r.id}>
                      <TableRow
                        data-testid={`row-invocation-${r.id}`}
                        className="cursor-pointer"
                        onClick={() => toggleExpand(r.id)}
                      >
                        <TableCell className="w-8 text-muted-foreground">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {new Date(r.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-medium">{tool?.displayName || r.toolId}</TableCell>
                        <TableCell className="text-xs text-muted-foreground" data-testid={`text-integration-${r.id}`}>
                          {integrationLabel}
                        </TableCell>
                        <TableCell className="text-xs">{chatbotNameById.get(r.chatbotId) || r.chatbotId.slice(0, 8)}</TableCell>
                        <TableCell className="text-xs">{userLabelById.get(r.userId) || r.userId.slice(0, 8)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            {statusBadge(r.status)}
                            {custom && <OkBadge ok={custom.ok} />}
                            {custom && <HttpStatusBadge status={custom.status} />}
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {r.durationMs != null ? `${r.durationMs} ms` : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                          {r.errorMessage || ""}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow
                          key={`${r.id}-details`}
                          data-testid={`row-invocation-details-${r.id}`}
                          className="bg-muted/40"
                        >
                          <TableCell />
                          <TableCell colSpan={8} className="p-3">
                            {custom && <CustomDetailPanel rowId={r.id} custom={custom} />}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <div className="text-xs font-medium text-muted-foreground mb-1">Input arguments</div>
                                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                                <pre tabIndex={0} role="region" aria-label="Input arguments" className="text-xs bg-background border rounded p-2 overflow-auto max-h-72 whitespace-pre-wrap break-all">
                                  {prettify(r.argsJson)}
                                </pre>
                              </div>
                              <div>
                                <div className="text-xs font-medium text-muted-foreground mb-1">Response</div>
                                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                                <pre tabIndex={0} role="region" aria-label="Response" className="text-xs bg-background border rounded p-2 overflow-auto max-h-72 whitespace-pre-wrap break-all">
                                  {prettify(r.resultJson)}
                                </pre>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
